import React, { useState, useEffect, useMemo } from 'react';

import { useTheme, makeStyles } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import Alert from '@material-ui/lab/Alert';
import Typography from '@material-ui/core/Typography';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';

import CloseIcon from '@material-ui/icons/Close';

import states from 'app/utils/States';
import Loading from 'app/components/loaders/Loading';
import RedButton from 'app/components/buttons/RedButton';

import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    grid: {
        margin: '0 -12px'
    },
    marginRight: {
        marginRight: theme.spacing(1)
    },
    alert: {
        marginTop: theme.spacing(2)
    },
    notActive: {
        paddingTop: theme.spacing(6),
        paddingBottom: theme.spacing(6),
        textAlign: 'center'
    },
    actions: {
        justifyContent: 'space-between'
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
}));

export default function EditPaymentMethodDialog(props) {

    const theme = useTheme();
    const api = useApi();
    const classes = useStyles();

    const [ values, setValues ] = useState(null);

    const [ errors, setErrors ] = useState([]);
    const [ loading, setLoading ] = useState(false);
    const [ changeHandled, setChangeHandled ] = useState(false);

    const { open, onClose, onClosed, onSave, urlPrefix, method, defaultMethodId } = props;

    const getPaymentMethod = () => {
        setLoading(true);

        api.get(`${urlPrefix}/payment/method/${method}`)
            .then(res => mapPaymentMethod(res.method))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        if (method && typeof method === 'object' && method !== null) {
            mapPaymentMethod(method);
        } else if (method && typeof method === 'string') {
            getPaymentMethod();
        } else {
            setValues(null);
        }
    }, [method]);

    const mapPaymentMethod = pm => {
        setValues({
            id: pm.id,
            type: pm.type,
            name: pm.billing_details.name,
            line1: pm.billing_details.address.line1,
            line2: pm.billing_details.address.line2 || '',
            city: pm.billing_details.address.city,
            state: pm.billing_details.address.state,
            postal_code: pm.billing_details.address.postal_code,
            country: pm.billing_details.address.country || 'US',
            exp_month: pm?.card?.exp_month,
            exp_year: pm?.card?.exp_year
        })
    }

    const handleChange = name => event => {
        const target = event.target;
        const value = (target.type === 'checkbox' ? target.checked : target.value);

        if (isError(name)) delete errors[name];

        setValues({ ...values, [name]: value });
        setChangeHandled(true);
    };

    const postPaymentMethod = () => {
        setLoading(true);
        setErrors([]);

        api.post(`${urlPrefix}/method/${values.id}`, values)
            .then(onSave)
            .catch(error => {
                setLoading(false)

                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            });
    }

    const postDefaultMethod = () => {
        setLoading(true);
        setErrors([]);

        api.post(`${urlPrefix}/payment/method/${values.id}/default`)
            .then(onSave)
            .catch(error => {
                setLoading(false)

                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            });
    }

    const deletePaymentMethod = () => {
        setLoading(true);
        setErrors([]);

        api.delete(`${urlPrefix}/payment/method/${values.id}`)
            .then(onSave)
            .catch(error => {
                setLoading(false)

                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            });
    }

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    const handleClosed = () => {
        setLoading(false);
        setErrors([]);
        if (onClosed) onClosed();
    }

    const isDefaultMethod = useMemo(() => {
        if (values && values.id && values.id === defaultMethodId) return true;
        return false;
    }, [values, defaultMethodId])

    return (
        <Dialog
            fullWidth
            maxWidth="sm"
            open={open}
            onClose={onClose}
            onExited={handleClosed}
            fullScreen={useMediaQuery(theme.breakpoints.down('sm'))}
            aria-labelledby="form-edit-card"
            >
            <Loading isLoading={loading}/>
            <DialogTitle id="form-edit-card">
                <span>Edit Payment Method</span>
                <IconButton aria-label="Cancel" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <DialogContentText gutterBottom>
                    Update your payment information below.
                </DialogContentText>
                {values ? (
                    <Grid container spacing={3} className={classes.grid}>
                        <Grid item xs={12}>
                            <TextField
                                id="input-name"
                                label="Account Holder Name"
                                value={values.name}
                                onChange={handleChange('name')}
                                fullWidth
                                error={isError('name')}
                                helperText={getErrorMessage('name')}
                                variant="outlined"
                                />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                id="input-address-one"
                                label="Address One"
                                value={values.line1}
                                onChange={handleChange('line1')}
                                fullWidth
                                error={isError('line1')}
                                helperText={getErrorMessage('line1')}
                                variant="outlined"
                                />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                id="input-address-two"
                                label="Address Two"
                                value={values.line2}
                                onChange={handleChange('line2')}
                                fullWidth
                                error={isError('line2')}
                                helperText={getErrorMessage('line2')}
                                variant="outlined"
                                />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                id="input-city"
                                label="City"
                                fullWidth
                                value={values.city}
                                onChange={handleChange('city')}
                                error={isError('city')}
                                helperText={getErrorMessage('city')}
                                variant="outlined"
                                />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <TextField
                                id="input-state"
                                select
                                fullWidth
                                label="State"
                                value={values.state}
                                onChange={handleChange('state')}
                                error={isError('state')}
                                helperText={getErrorMessage('state')}
                                variant="outlined"
                                >
                                {states.map( state => {
                                    return (
                                        <MenuItem key={state.abbreviation} value={state.abbreviation} >
                                            {state.abbreviation}
                                        </MenuItem>
                                    )
                                })}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                id="input-zip"
                                label="Zip"
                                fullWidth
                                value={values.postal_code}
                                onChange={handleChange('postal_code')}
                                error={isError('postal_code')}
                                helperText={getErrorMessage('postal_code')}
                                variant="outlined"
                            />
                        </Grid>
                        {(values.type === 'card') &&
                            <>
                                <Grid item xs={6}>
                                    <TextField
                                        id="input-exp-month"
                                        label="Expiration Month"
                                        fullWidth
                                        type="number"
                                        value={values.exp_month}
                                        onChange={handleChange('exp_month')}
                                        error={isError('exp_month')}
                                        helperText={getErrorMessage('exp_month')}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        id="input-exp-year"
                                        label="Expiration Year"
                                        fullWidth
                                        type="number"
                                        value={values.exp_year}
                                        onChange={handleChange('exp_year')}
                                        error={isError('exp_year')}
                                        helperText={getErrorMessage('exp_year')}
                                        variant="outlined"
                                    />
                                </Grid>
                            </>
                        }
                        {isError('stripe') &&
                            <Grid item xs={12}>
                                <Alert variant="filled" severity="error" className={classes.alert}>
                                    {getErrorMessage('stripe')}
                                </Alert>
                            </Grid>
                        }
                    </Grid>
                ) : (
                    <Typography
                        className={classes.notActive}
                        variant="subtitle2"
                        color="error"
                        >
                        Please select a payment method to update or delete.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions className={classes.actions}>
                <Button
                    color="primary"
                    onClick={postDefaultMethod}
                    disabled={loading || isDefaultMethod}
                    >
                    Make Default
                </Button>
                <div>
                    <RedButton
                        onClick={deletePaymentMethod}
                        className={classes.marginRight}
                        disabled={loading}
                        >
                        Delete
                    </RedButton>
                    <Button
                        onClick={postPaymentMethod}
                        color="primary"
                        disabled={loading || !changeHandled}
                        variant="contained"
                        >
                        Update
                    </Button>
                </div>
            </DialogActions>
        </Dialog>
    )
}
